/*
 * A ponte com o ramon/chat: o tema nunca importa um módulo dele (sem a
 * extensão, o import quebraria o tema inteiro). Tudo vem do registro do
 * Flarum (`flarum.reg`), onde o ramon/chat registra cada módulo com o
 * caminho do arquivo (`forum/state/chat`, `forum/utils/surface`…), e só
 * quando `ramon-chat` está em `flarum.extensions`.
 *
 * Os tipos aqui são só o pedaço da API dele que o tema usa — o bastante para
 * o TypeScript, sem acoplar o tema às classes da extensão.
 */
import app from 'flarum/forum/app';
import { isExtensionEnabled } from '../../utils/settings';
export const NAMESPACE = 'ramon-chat';

/** O pedaço do modelo `chat-channels` do ramon/chat que a barra lateral lê. */

/** O pedaço do `ChatState` (o estado global do chat, `forum/state/chat`) que o tema usa. */

/** Um módulo do ramon/chat já registrado, ou `null` (extensão desligada ou chunk ainda não baixado). */
export function ramonModule(id) {
  if (!isExtensionEnabled(NAMESPACE)) return null;
  try {
    return flarum.reg.checkModule(NAMESPACE, id) || null;
  } catch {
    return null;
  }
}

/** O estado global do chat: o mesmo objeto que a página, a gaveta e o tempo real do ramon/chat usam. */
export function chatState() {
  return ramonModule('forum/state/chat');
}

/**
 * O leitor vê o chat: a mesma regra do `canUseChat()` do ramon/chat (que não
 * é exportada) — permissão no fórum, logado e sem ter desligado o chat nas
 * configurações.
 */
export function canUseChat() {
  if (!isExtensionEnabled(NAMESPACE) || !app.forum || !app.session.user) return false;
  if (!app.forum.attribute('canUseChat')) return false;
  return app.session.user.preferences()?.['ramon-chat.enabled'] !== false;
}

/** Estamos na página cheia do chat (o ChatPage do ramon/chat põe esta classe no <body>). */
export function onChatPage() {
  return document.body.classList.contains('ChatFullPage');
}

/** A gaveta ou a página cheia, pela mesma regra do botão do cabeçalho do ramon/chat. */
function prefersDrawer() {
  const surface = ramonModule('forum/utils/surface');
  try {
    return !!surface?.shouldUseChatDrawer?.();
  } catch {
    return false;
  }
}

/** Abre a gaveta do ramon/chat (o `ChatDrawer.open()` estático dele). */
function openDrawer() {
  const drawer = ramonModule('forum/components/ChatDrawer');
  if (!drawer?.open) return false;
  drawer.open().catch(() => {});
  return true;
}

/** O endereço de um canal, para o `href` (abrir em nova aba continua funcionando). */
export function channelUrl(id) {
  return app.route('chat.channel', {
    id
  });
}

/**
 * Abre um canal: na gaveta, para quem a usa e não está na
 * página cheia; senão, na página do chat. No celular a barra lateral está
 * dentro do painel do hambúrguer — a navegação o fecha (o `Page` do core).
 */
export function openChannel(id) {
  const state = chatState();
  if (!state) return;
  state.setActiveChannel(id);
  if (!onChatPage() && prefersDrawer() && openDrawer()) {
    m.redraw();
    return;
  }
  m.route.set(channelUrl(id));
}

/** Uma das telas do chat (`chat.threads`, `chat.search`, `chat.browse`…) na página cheia. */
export function openRoute(name) {
  m.route.set(app.route(name));
}

/**
 * Abre (ou cria) a conversa direta com estas pessoas — o mesmo fluxo do
 * `startDirectMessage` do ramon/chat, que só aceita uma pessoa: aqui cabem
 * várias (a MD em grupo). O POST devolve o canal serializado.
 */
export async function startDirect(userIds) {
  const state = chatState();
  if (!state || !userIds.length) return false;
  const payload = await app.request({
    method: 'POST',
    url: `${app.forum.attribute('apiUrl')}/chat/direct`,
    body: {
      data: {
        attributes: {
          userIds
        }
      }
    }
  });
  const id = Number(payload?.data?.id);
  if (!id) return false;
  let channel = null;
  if (payload?.meta?.serialized) {
    try {
      channel = app.store.pushPayload(payload);
    } catch {
      channel = null;
    }
  }
  if (!channel) channel = (await app.store.find('chat-channels', String(id)).catch(() => null)) || null;
  if (channel) state.rememberChannel(channel);
  if (payload?.meta?.created) state.seedEmptyChannel(id);
  openChannel(id);
  return true;
}

/** O ícone do canal do próprio ramon/chat (emoji, imagem ou os avatares da MD). */
export function ramonChannelIcon(channel, className) {
  // O registro guarda o módulo como a própria função quando ele só exporta
  // ela; com mais exportações, como objeto.
  const helper = ramonModule('forum/utils/channelIcon');
  const fn = typeof helper === 'function' ? helper : helper?.channelIcon;
  try {
    return fn ? fn(channel, className) : null;
  } catch {
    return null;
  }
}

/**
 * O "x" da MD na lista: sai da conversa pelo POST /leave do ramon/chat (o mesmo
 * da ação "Sair" dele) e a tira da lista. Devolve se saiu.
 */
export async function leaveDirect(channel) {
  const state = chatState();
  try {
    await app.request({
      method: 'POST',
      url: `${app.forum.attribute('apiUrl')}/chat-channels/${channel.id()}/leave`
    });
  } catch (e) {
    app.alerts.show({
      type: 'error'
    }, e?.response?.errors?.[0]?.detail ?? app.translator.trans('ramon-chat.forum.channel.leave_failed', {}, true));
    return false;
  }
  channel.pushAttributes?.({
    isFollowing: false,
    unreadCount: 0,
    unreadMentionsCount: 0
  });
  if (state) {
    // Depois da animação de saída da linha.
    setTimeout(() => {
      state.channels = state.channels.filter(c => c.id() !== channel.id());
      if (state.activeChannelId === Number(channel.id())) state.setActiveChannel(null);
      m.redraw();
    }, 250);
  }
  return true;
}
flarum.reg.add('ramon-canela', 'forum/thirdparty/ramonChat/bridge', { NAMESPACE: NAMESPACE,ramonModule: ramonModule,chatState: chatState,canUseChat: canUseChat,onChatPage: onChatPage,channelUrl: channelUrl,openChannel: openChannel,openRoute: openRoute,ramonChannelIcon: ramonChannelIcon, });